import {
  Box,
  Button,
  Flex,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { FormikProps, getIn } from 'formik';
import * as _ from 'lodash';
import React, { useCallback, useState } from 'react';

import { checkWaiveConfigErrors } from '@finley-technologies/calc-agent';

import { WipConfig } from '../../types';
import { CreditAgreement, getBooleanSymbol, getIterationKey } from '../common';
import { ConcentrationLimitConfigConditionalProperties } from './ConcentrationLimitConfigConditionalProperties';
import { useDraggableRow } from 'packages/common-frontend/components/DragAndDropTable/hooks';
import * as DragAndDropTableTypes from 'packages/common-frontend/components/DragAndDropTable/types';
import { TrDraggableHandle } from 'packages/common-frontend/components/DragAndDropTable/ui';
import { InputFormField } from 'packages/common-frontend/components/FormField';
import { Icon as FinleyIcon } from 'packages/common-frontend/components/icons';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditButton';
import { EditPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPropertyModal';
import './concentrationLimitConfig.scss';

const headers: (keyof CreditAgreement.DomainEntity.ConcentrationLimitProperty)[] = [
  'name',
  'criteriaNumber',
  'section',
  '_note_',
  '_config_',
];

export const createConcentrationLimitConfigShell = (
  name?: string,
): CreditAgreement.Config.ConcentrationLimit.SourceConfig => ({
  propertyKey: name ? _.camelCase(name) : 'placeholder',
  calculationConfig: {
    type: 'standard',
    config: {
      filters: [],
    },
  },
  valueConfig: {
    type: 'formula',
    limit: '1',
    valueType: 'number',
  },
  waiveConfigs: [],
});

export const createConcentrationLimitPropertyShell = (): CreditAgreement.DomainEntity.ConcentrationLimitProperty => ({
  name: 'Placeholder',
  type: CreditAgreement.DomainEntity.PropertyType.ConcentrationLimitProperty,
  value: 'Placeholder',
  criteriaNumber: '',
  _isWrittenIn_: true,
  _config_: {},
});

type ConcentrationLimitPropertyProps = {
  property: CreditAgreement.DomainEntity.ConcentrationLimitProperty;
  basePropertyKey: string;
  idx: number;
  wipConfig?: WipConfig;
  id: string;
  moveRow: DragAndDropTableTypes.MoveRow;
  isDragDisabled: boolean;
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[];
  currentCommittedConcentrationLimitKeys: string[];
};
const ConcentrationLimitProperty = ({
  isDragDisabled,
  id,
  moveRow,
  basePropertyKey,
  idx,
  property,
  wipConfig,
  availableFields,
  currentCommittedConcentrationLimitKeys,
}: ConcentrationLimitPropertyProps) => {
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [editorErrors, setEditorErrors] = useState<string[]>([]);

  const configPrefix = '_config_';
  const concentrationLimitConfigPrefix = 'concentrationLimitConfig';
  const fieldValuePrefix = `${configPrefix}.${concentrationLimitConfigPrefix}`;

  const concentrationLimitKey = `${basePropertyKey}[${idx}]._config_.concentrationLimitConfig`;

  const validateConditionalProperties = useCallback(
    (values: CreditAgreement.DomainEntity.ConcentrationLimitProperty) => {
      if (editorErrors) {
        return editorErrors;
      }
      return values._config_.concentrationLimitConfig
        ? _.flatMap(
            checkWaiveConfigErrors({
              waiveConfigs:
                values._config_.concentrationLimitConfig.waiveConfigs,
            }),
            (waiveConfig) => waiveConfig.errorMessages,
          )
        : [];
    },
    [editorErrors],
  );

  const onAddConditionalProperties = useCallback(
    (localFormProps: FormikProps<any>) => {
      const currentName = localFormProps.values.name;

      localFormProps.setFieldValue(configPrefix, {
        concentrationLimitConfig: createConcentrationLimitConfigShell(
          currentName,
        ),
      });
    },
    [configPrefix],
  );

  const onDeleteConditionalProperties = useCallback(
    (localFormProps: FormikProps<any>) => {
      localFormProps.setFieldValue(configPrefix, {});
    },
    [configPrefix],
  );

  const getIsPropertyKeyCurrentlyCommitted = useCallback(
    (localFormProps: FormikProps<any>) => {
      if (
        localFormProps.values[configPrefix]?.[concentrationLimitConfigPrefix]
          ?.propertyKey
      ) {
        return _.includes(
          currentCommittedConcentrationLimitKeys,
          localFormProps.values[configPrefix][concentrationLimitConfigPrefix]
            .propertyKey,
        );
      }
      return false;
    },
    [currentCommittedConcentrationLimitKeys],
  );

  const ConditionalPropertiesComponent = useCallback(
    (localFormProps: FormikProps<any>, validationErrors: Array<string>) => {
      return (
        <ConcentrationLimitConfigConditionalProperties
          localFormProps={localFormProps}
          validationErrors={validationErrors}
          wipConfig={wipConfig}
          concentrationLimitKey={concentrationLimitKey}
          configPrefix={configPrefix}
          concentrationLimitConfigPrefix={concentrationLimitConfigPrefix}
          fieldValuePrefix={fieldValuePrefix}
          setEditorErrors={setEditorErrors}
          availableFields={availableFields}
        />
      );
    },
    [fieldValuePrefix, wipConfig, concentrationLimitKey, availableFields],
  );

  return (
    <TrDraggableHandle
      id={id}
      acceptedType="concentrationLimitPropert"
      index={idx}
      moveRow={moveRow}
      isDragDisabled={isDragDisabled}
    >
      <>
        {_.map(headers, (header) => {
          if (header === '_config_') {
            return (
              <Td key={`${basePropertyKey}[${idx}].${header}`}>
                {getBooleanSymbol(
                  Boolean(property._config_?.concentrationLimitConfig),
                )}
              </Td>
            );
          }
          return (
            <Td
              key={`${basePropertyKey}.concentrationLimits[${idx}].${header}`}
            >
              {property[header] ? property[header] : '-'}
            </Td>
          );
        })}
        {wipConfig?.isMutateAllowed(`${basePropertyKey}[${idx}]`) &&
          isDragDisabled && (
            <Td>
              <EditButton setIsOpen={setIsEditOpen} />
              <EditPropertyModal
                isOpen={isEditOpen}
                setIsOpen={setIsEditOpen}
                wipConfig={wipConfig}
                basePropertyKey={`${basePropertyKey}[${idx}]`}
                modalHeaderLabel="Concentration Limit"
                conditionalProperties={{
                  conditionalPropertiesComponent: ConditionalPropertiesComponent,
                  conditionalPropertiesToggleLabel: 'Configuration',
                  conditionalPropertiesValidation: validateConditionalProperties,
                  useConditionalProperties: !!property._config_
                    ?.concentrationLimitConfig,
                  onDeleteConditionalProperties: (
                    localFormProps: FormikProps<any>,
                  ) => onDeleteConditionalProperties(localFormProps),
                  onAddConditionalProperties: (
                    localFormProps: FormikProps<any>,
                  ) =>
                    wipConfig?.isMutateAllowed(
                      `${basePropertyKey}[${idx}]._config_`,
                    ) && onAddConditionalProperties(localFormProps),
                  getIsPropertyKeyCurrentlyCommitted: (
                    localFormProps: FormikProps<any>,
                  ) => getIsPropertyKeyCurrentlyCommitted(localFormProps),
                }}
              >
                {(localFormProps) => (
                  <>
                    <InputFormField
                      propName="value"
                      label="Value"
                      formProps={localFormProps}
                      value={localFormProps.values.value}
                      placeholder="Enter value"
                    />
                    <InputFormField
                      propName="criteriaNumber"
                      label="Criteria Number"
                      formProps={localFormProps}
                      value={localFormProps.values.criteriaNumber}
                      placeholder="Enter criteria number"
                    />
                  </>
                )}
              </EditPropertyModal>

              <DeleteArrayPropertyButton
                wipConfig={wipConfig}
                basePropertyKey={basePropertyKey}
                idx={idx}
              />
            </Td>
          )}
      </>
      {/* )} */}
    </TrDraggableHandle>
  );
};

type LoanProductConcentrationLimitsProps = {
  concentrationLimits: CreditAgreement.DomainEntity.ConcentrationLimitProperty[];
  basePropertyKey: string;
  idx: number;
  wipConfig?: WipConfig;
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[];
  currentCommittedConcentrationLimitKeys: string[];
};
export const LoanProductConcentrationLimits = ({
  concentrationLimits,
  basePropertyKey,
  wipConfig,
  idx,
  availableFields,
  currentCommittedConcentrationLimitKeys,
}: LoanProductConcentrationLimitsProps) => {
  const {
    moveRow,
    useRowState,
    useOrderLockedState,
    ui: { LockOrderButton, RestoreOrderButton, SaveOrderCallout },
  } = useDraggableRow<CreditAgreement.DomainEntity.ConcentrationLimitProperty>({
    comparisonKey: 'name',
  });

  const [concentrationLimitsState] = useRowState(concentrationLimits);
  const [isOrderLocked] = useOrderLockedState();

  const fullPropertyKey = `${basePropertyKey}[${idx}].concentrationLimits`;

  const handleAdd = () => {
    const newConcentrationLimit = createConcentrationLimitPropertyShell();
    const previousConcentrationLimits = getIn(
      wipConfig?.formProps.values,
      fullPropertyKey,
    );
    wipConfig?.formProps.setFieldValue(fullPropertyKey, [
      ...previousConcentrationLimits,
      newConcentrationLimit,
    ]);
  };

  const onSaveOrder = useCallback(() => {
    wipConfig?.formProps.setFieldValue(
      `borrowingBaseMechanics.calcAgentMechanics.loanProductMechanics[${idx}].concentrationLimits`,
      concentrationLimitsState,
    );
  }, [wipConfig, concentrationLimitsState, idx]);

  return (
    <Box mb="35px" width="100%">
      <Flex mb="10px" alignItems="center">
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <LockOrderButton onSaveOrder={onSaveOrder} />
        )}
        <Text fontWeight="bold" fontSize="20px">
          Concentration Limits
        </Text>
        {wipConfig?.isMutateAllowed(fullPropertyKey) && <RestoreOrderButton />}
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <Spacer />
            <Button
              aria-label="Add Concentration Limit"
              minW="fit-content"
              borderWidth="2px"
              colorScheme="v2-aurora"
              variant="secondary"
              onClick={() => handleAdd()}
              isDisabled={!isOrderLocked}
            >
              <FinleyIcon icon="plus" />
              <Text ml="16px">Add Concentration Limit</Text>
            </Button>
          </>
        )}
      </Flex>
      {wipConfig?.isMutateAllowed(fullPropertyKey) && <SaveOrderCallout />}
      {!_.isEmpty(concentrationLimitsState) ? (
        <Flex width="100%" overflowY="auto">
          <Table borderWidth="1px">
            <Thead>
              <Tr>
                {wipConfig && !isOrderLocked && <Th />}
                {headers.map((header) => (
                  <Th
                    key={header}
                    minWidth={header === 'name' ? '15rem' : '10rem'}
                  >
                    {_.startCase(header)}
                  </Th>
                ))}
                {wipConfig && isOrderLocked && (
                  <Th width="8.5rem" minWidth="8.5rem" />
                )}
              </Tr>
            </Thead>
            <Tbody>
              {concentrationLimitsState.map((concentrationLimit, testIdx) => {
                const id = getIterationKey(concentrationLimit);
                return (
                  <ConcentrationLimitProperty
                    key={id}
                    property={concentrationLimit}
                    wipConfig={wipConfig}
                    basePropertyKey={fullPropertyKey}
                    idx={testIdx}
                    id={id}
                    moveRow={moveRow}
                    isDragDisabled={isOrderLocked}
                    availableFields={availableFields}
                    currentCommittedConcentrationLimitKeys={
                      currentCommittedConcentrationLimitKeys
                    }
                  />
                );
              })}
            </Tbody>
          </Table>
        </Flex>
      ) : (
        <Text>There are no concentration limits currently configured</Text>
      )}
    </Box>
  );
};
